import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createRulesApi } from '../../../../flux/facades/rulesApi';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { apiErrorDetails, detectionLabel, errorMessage } from '../../../Rules/ruleHelpers';

// Assigning and unassigning rules for this project. Rule definitions
// themselves live on the standalone Rules page and are org-owned — this tab
// only points an existing org rule at (or away from) this project.
export const ProjectRules = ({ projectId, isAdmin }) => {
    const { getAccessToken } = useApiAccessToken();
    const rulesApi = useMemo(() => createRulesApi(getAccessToken), [getAccessToken]);

    const [assignedRules, setAssignedRules] = useState([]);
    const [orgRules, setOrgRules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [pendingRuleId, setPendingRuleId] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);

    const loadRules = useCallback(async () => {
        setIsLoading(true);
        setPageError(null);
        try {
            const [assigned, allRules] = await Promise.all([
                rulesApi.getProjectRules(projectId),
                isAdmin ? rulesApi.getRules() : Promise.resolve([]),
            ]);
            setAssignedRules(assigned);
            setOrgRules(allRules);
        } catch (error) {
            setPageError(errorMessage(error, 'load'));
        } finally {
            setIsLoading(false);
        }
    }, [rulesApi, projectId, isAdmin]);

    useEffect(() => {
        loadRules();
    }, [loadRules]);

    const assignedRuleIds = new Set(assignedRules.map((entry) => entry.rule.object_id));
    const assignableRules = orgRules.filter((rule) => !assignedRuleIds.has(rule.object_id));

    const activate = async (rule) => {
        setPendingRuleId(rule.object_id);
        setNotice(null);
        try {
            await rulesApi.assignRuleToProject(projectId, rule.object_id);
            setNotice({ type: 'success', message: `${rule.name} activated.` });
            await loadRules();
        } catch (error) {
            if (apiErrorDetails(error).status === 404) await loadRules();
            else setPageError(errorMessage(error, 'activate'));
        } finally {
            setPendingRuleId(null);
        }
    };

    const deactivate = async (entry) => {
        const rule = entry.rule;
        setPendingRuleId(rule.object_id);
        setNotice(null);
        try {
            await rulesApi.unassignRuleFromProject(projectId, rule.object_id);
            setAssignedRules((current) => current.filter((item) => item.rule.object_id !== rule.object_id));
            setNotice({ type: 'success', message: `${rule.name} deactivated.` });
        } catch (error) {
            if (apiErrorDetails(error).status === 404) await loadRules();
            else setPageError(errorMessage(error, 'deactivate'));
        } finally {
            setPendingRuleId(null);
        }
    };

    return (
        <div>
            <div className="rules-header">
                <div>
                    <h2 className="pw-page-title">Active rules</h2>
                    <p className="rules-page-subtitle">Rules currently enforced on this project.</p>
                </div>
                {isAdmin && (
                    <button type="button" className="rules-btn-primary" onClick={() => { setNotice(null); setPickerOpen(true); }}>
                        + Activate a rule
                    </button>
                )}
            </div>

            {notice && <div className={`rules-alert rules-alert-${notice.type}`} role="status">{notice.message}</div>}
            {pageError && (
                <div className="rules-alert rules-alert-error" role="alert">
                    <span>{pageError}</span>
                    <button type="button" className="rules-btn-ghost" onClick={loadRules}>Retry</button>
                </div>
            )}

            {isLoading ? (
                <p className="projects-loading">Loading active rules…</p>
            ) : assignedRules.length === 0 ? (
                <div className="rules-empty"><p>No active rules yet.</p></div>
            ) : (
                <div className="rules-table-wrapper">
                    <table className="rules-table">
                        <thead>
                            <tr>
                                <th className="rules-th">Name</th>
                                <th className="rules-th">First entity</th>
                                <th className="rules-th">Second entity</th>
                                <th className="rules-th">Effect</th>
                                <th className="rules-th">Detection</th>
                                {isAdmin && <th className="rules-th">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {assignedRules.map((entry) => (
                                <tr key={entry.rule.object_id} className="rules-tr">
                                    <td className="rules-td rules-td-name">{entry.rule.name}</td>
                                    <td className="rules-td">{entry.rule.definition.firstEntity.entityType}</td>
                                    <td className="rules-td">{entry.rule.definition.secondEntity?.entityType ?? '—'}</td>
                                    <td className="rules-td"><span className={`rules-effect rules-effect-${entry.rule.effect}`}>{entry.rule.effect}</span></td>
                                    <td className="rules-td rules-detection-cell">{detectionLabel(entry.rule)}</td>
                                    {isAdmin && (
                                        <td className="rules-td">
                                            <button
                                                type="button"
                                                className="rules-action-button rules-action-danger"
                                                disabled={pendingRuleId === entry.rule.object_id}
                                                onClick={() => deactivate(entry)}
                                            >
                                                {pendingRuleId === entry.rule.object_id ? 'Deactivating…' : 'Deactivate'}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pickerOpen && (
                <div className="members-modal-backdrop" role="presentation">
                    <div className="members-modal members-modal-wide" role="dialog" aria-modal="true" aria-labelledby="activate-project-rule-title">
                        <h2 id="activate-project-rule-title">Activate a rule</h2>
                        <div className="members-modal-body">
                            {assignableRules.length === 0 ? (
                                <p>
                                    {orgRules.length === 0
                                        ? <>No rules exist yet. <Link to="/rules">Create one</Link> first.</>
                                        : 'Every rule is already active on this project.'}
                                </p>
                            ) : (
                                <div className="rules-table-wrapper">
                                    <table className="rules-table">
                                        <thead>
                                            <tr>
                                                <th className="rules-th">Name</th>
                                                <th className="rules-th">Effect</th>
                                                <th className="rules-th">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignableRules.map((rule) => (
                                                <tr key={rule.object_id} className="rules-tr">
                                                    <td className="rules-td rules-td-name">{rule.name}</td>
                                                    <td className="rules-td"><span className={`rules-effect rules-effect-${rule.effect}`}>{rule.effect}</span></td>
                                                    <td className="rules-td">
                                                        <button
                                                            type="button"
                                                            className="rules-action-button"
                                                            disabled={pendingRuleId === rule.object_id}
                                                            onClick={() => activate(rule)}
                                                        >
                                                            {pendingRuleId === rule.object_id ? 'Activating…' : 'Activate'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="pw-actions">
                            <button type="button" className="rules-btn-ghost" onClick={() => setPickerOpen(false)}>Done</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectRules;
